import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import FileUpload from '../../helper/FileUpload';
import uploadIcon from '/src/assets/images/application/folder-file-up.svg?url';
interface ImageUploadContainerProps {
  onImageUpload: (files: File[]) => void; // Callback to pass files to parent
}

const ImageUploadContainer: React.FC<ImageUploadContainerProps> = ({
  onImageUpload,
}) => {
  const [files, setFiles] = useState<File[]>([]);

  // One object url per File, cached and revoked on unmount. Creating them
  // inline in render mints a fresh url on every pass and never frees it.
  const previewUrlsRef = useRef(new Map<File, string>());

  const previewUrl = (file: File): string => {
    const cached = previewUrlsRef.current.get(file);

    if (cached) {
      return cached;
    }

    const url = URL.createObjectURL(file);
    previewUrlsRef.current.set(file, url);

    return url;
  };

  useEffect(() => {
    const urls = previewUrlsRef.current;

    return () => {
      urls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
      urls.clear();
    };
  }, []);

  // Handling file uploads
  const handleFileUpload = (newFiles: File[]) => {
    // The api accepts jpeg/jpg/png, so anything else is dropped here rather
    // than failing validation after the upload starts.
    const validFiles = newFiles.filter((file) =>
      ['image/png', 'image/jpeg'].includes(file.type),
    );

    if (validFiles.length === 0) {
      return;
    }

    setFiles((prevFiles) => [...prevFiles, ...validFiles]);
  };

  // Handling file removal
  const handleRemoveFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  // Handling drag and drop
  const handleDragEnd = (result: any) => {
    const { source, destination } = result;
    if (!destination) return;

    const reorderedFiles = Array.from(files);
    const [removed] = reorderedFiles.splice(source.index, 1);
    reorderedFiles.splice(destination.index, 0, removed);

    setFiles(reorderedFiles);
  };

  // Notify parent component only when files change
  useEffect(() => {
    if (files.length > 0) {
      onImageUpload(files);
    }
  }, [files]); // Dependency array ensures it runs only when files change

  return (
    <Box
      sx={{
        display: 'flex',
        flexShrink: 0,
        width: '100%',
        height: '229px',
        border: '1px solid #E5E5E8',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: '#f9f9f9',
        boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.1)',
      }}
    >
      <FileUpload
        height="229px"
        width="150px"
        direction="rtl"
        flexDirection="row"
        showFileInfo={false}
        onFilesSelected={handleFileUpload}
        imageSrc={uploadIcon}
        backgroundColor="#F0F0F0"
        uploadText="Upload Icon"
        acceptedFileTypes={['.png', '.jpg', '.jpeg']}
      />
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="droppable" direction="horizontal">
          {(provided) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '4px',
                marginTop: '1px',
                overflowX: 'auto',
                width: '100%',
                whiteSpace: 'nowrap',
              }}
            >
              {files.map((file, index) => (
                <Draggable
                  key={file.name}
                  draggableId={file.name}
                  index={index}
                >
                  {(provided) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #E5E5E8',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        height: '229px',
                        width: '150px',
                        position: 'relative',
                        flexShrink: 0,
                        backgroundColor: '#f9f9f9',
                      }}
                    >
                      <img
                        src={previewUrl(file)}
                        alt={file.name}
                        style={{
                          height: '100%',
                          width: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          backgroundColor: 'rgba(0,0,0,0.5)',
                          color: '#fff',
                        }}
                        onClick={() => handleRemoveFile(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
};

export default ImageUploadContainer;
